import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rupee } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Truck, Tag, Loader2 } from "lucide-react";

export default function Checkout() {
  usePageMeta("Checkout", "Complete your order with Cash on Delivery.");
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [locationId, setLocationId] = useState("");
  const [address, setAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [notes, setNotes] = useState("");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/delivery-locations").then(({ data }) => setLocations(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length === 0 && !result) navigate("/products");
  }, [items, result, navigate]);

  const selectedLoc = locations.find((l) => l.id === locationId);
  const threshold = settings?.free_delivery_threshold || 0;
  let deliveryCharge = selectedLoc ? selectedLoc.charge : settings?.default_delivery_charge || 0;
  const netAfterDiscount = subtotal - discount;
  if (threshold && netAfterDiscount >= threshold) deliveryCharge = 0;
  const total = Math.max(0, netAfterDiscount + deliveryCharge);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setDiscount(data.discount);
      setAppliedCoupon(data.code);
      toast.success(`Coupon ${data.code} applied — you saved ${rupee(data.discount)}`);
    } catch (e) {
      setDiscount(0);
      setAppliedCoupon(null);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const placeOrder = async () => {
    if (address.trim().length < 5) {
      toast.error("Please enter a valid delivery address");
      return;
    }
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        delivery_location_id: locationId || null,
        address,
        landmark,
        coupon_code: appliedCoupon || null,
        notes,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      setResult(data);
      const primary = data.seller_notifications?.[0]?.whatsapp_url || data.whatsapp_url;
      window.open(primary, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPlacing(false);
    }
  };

  if (result) {
    const order = result.order;
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-6">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage/15 text-sage">
            <CheckCircle2 className="h-9 w-9" />
          </span>
        </motion.div>
        <h1 className="mt-6 font-heading text-3xl font-bold text-ink">Order placed!</h1>
        <p className="mt-2 text-ink-soft">
          Your order <span className="font-semibold text-ink" data-testid="order-number">{order.order_number}</span> is confirmed for Cash on Delivery.
        </p>
        <p className="mt-1 text-sm text-ink-muted">Send us the pre-filled message on WhatsApp so we can pack it right away.</p>

        <div className="mt-8 flex flex-col items-center gap-3">
          <a href={result.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-order-btn">
            <Button className="rounded-full bg-[#25D366] px-8 text-white hover:bg-[#1fb55]">
              <MessageCircle className="mr-2 h-4 w-4" /> Send Order on WhatsApp
            </Button>
          </a>
          {result.seller_notifications?.length > 0 && (
            <div className="w-full max-w-md rounded-2xl border border-border/60 bg-white p-4 text-left" data-testid="seller-notify-block">
              <p className="text-sm font-semibold text-ink">Notify your seller{result.seller_notifications.length > 1 ? "s" : ""} on WhatsApp</p>
              <p className="text-xs text-ink-muted">Your order is sent directly to {result.seller_notifications.length} shop{result.seller_notifications.length > 1 ? "s" : ""}.</p>
              <div className="mt-3 space-y-2">
                {result.seller_notifications.map((s) => (
                  <a key={s.seller_id} href={s.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid={`notify-seller-${s.seller_id}`}>
                    <Button variant="outline" className="w-full justify-start rounded-full border-[#25D366] text-[#128C4A]">
                      <MessageCircle className="mr-2 h-4 w-4" /> Send to {s.shop_name}
                    </Button>
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Link to="/track">
              <Button variant="outline" className="rounded-full">Track My Order</Button>
            </Link>
            <Link to="/products">
              <Button variant="ghost" className="rounded-full text-ink-soft">Continue shopping</Button>
            </Link>
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-border/60 bg-white p-6 text-left">
          <p className="text-sm font-semibold text-ink">Order total: {rupee(order.total)}</p>
          <p className="mt-1 text-xs text-ink-muted">Delivering to: {order.address}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="font-heading text-3xl font-bold text-ink">Checkout</h1>
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
            <h2 className="font-heading text-lg text-ink">Delivery details</h2>
            <div className="mt-4 space-y-4">
              <div>
                <Label className="text-ink-soft">Name</Label>
                <Input value={user?.name || ""} disabled className="mt-1.5 rounded-xl bg-cream-alt/50" />
              </div>
              <div>
                <Label className="text-ink-soft">Mobile</Label>
                <Input value={user?.mobile || ""} disabled className="mt-1.5 rounded-xl bg-cream-alt/50" />
              </div>
              <div>
                <Label className="text-ink-soft">Delivery area</Label>
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="location-select">
                    <SelectValue placeholder={locations.length ? "Select your area" : "No areas configured"} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id} data-testid={`location-${l.id}`}>
                        {l.name} {l.charge > 0 ? `· ${rupee(l.charge)}` : "· Free"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-ink-soft">Full address</Label>
                <Textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="House / flat no, street, area, city, pincode"
                  className="mt-1.5 rounded-xl"
                  rows={3}
                  data-testid="address-input"
                />
              </div>
              <div>
                <Label className="text-ink-soft">Landmark (optional)</Label>
                <Input
                  value={landmark}
                  onChange={(e) => setLandmark(e.target.value)}
                  placeholder="e.g. Near Anjaneya Temple"
                  className="mt-1.5 rounded-xl"
                  data-testid="landmark-input"
                />
              </div>
              <div>
                <Label className="text-ink-soft">Order notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any delivery instructions?"
                  className="mt-1.5 rounded-xl"
                  rows={2}
                  data-testid="notes-input"
                />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
            <h2 className="flex items-center gap-2 font-heading text-lg text-ink"><Tag className="h-4 w-4 text-brand" /> Have a coupon?</h2>
            <div className="mt-3 flex gap-2">
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Enter code" className="rounded-xl" data-testid="coupon-input" />
              <Button onClick={applyCoupon} variant="outline" className="rounded-xl border-brand text-brand" data-testid="apply-coupon">Apply</Button>
            </div>
            {appliedCoupon && <p className="mt-2 text-sm text-sage">Applied {appliedCoupon} — saved {rupee(discount)}</p>}
          </section>
        </div>

        <aside className="h-fit rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
          <h2 className="font-heading text-lg text-ink">Summary</h2>
          <div className="mt-4 space-y-2 text-sm">
            {items.map((i) => (
              <div key={i.id} className="flex justify-between text-ink-soft">
                <span className="line-clamp-1">{i.name} × {i.quantity}</span>
                <span>{rupee((i.discount_price || i.price) * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="my-4 h-px bg-border" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-ink-soft"><span>Subtotal</span><span>{rupee(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-sage"><span>Discount</span><span>-{rupee(discount)}</span></div>}
            <div className="flex justify-between text-ink-soft">
              <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> Delivery</span>
              <span>{deliveryCharge > 0 ? rupee(deliveryCharge) : "Free"}</span>
            </div>
          </div>
          <div className="my-4 h-px bg-border" />
          <div className="flex justify-between text-lg font-bold text-ink">
            <span>Total</span>
            <span data-testid="checkout-total">{rupee(total)}</span>
          </div>
          <p className="mt-1 text-xs text-ink-muted">Payment: Cash on Delivery</p>
          <Button onClick={placeOrder} disabled={placing} className="mt-5 w-full rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="place-order-btn">
            {placing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Placing...</> : "Place Order (COD)"}
          </Button>
        </aside>
      </div>
    </div>
  );
}
